import { useEffect, useId, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  api,
  ErrorDeApi,
  type CaracterDeDeclaracion,
  type Cliente,
  type EtapaDeDeclaracion,
  type PresentacionDeDeclaracion,
  type ResumenDePapel,
  type RolDeUsuario,
  type SeguimientoDeDeclaracion,
  type SeguimientoDeDeclaraciones,
  type TipoDeDeclaracion,
} from '../servicios/api'
import { nombreDePeriodo, periodosDisponibles } from '../periodos'
import { fechaContable } from '../fechas'
import { PaqueteDeDeclaraciones } from '../componentes/PaqueteDeDeclaraciones'
import { PaqueteDePresentacion } from '../componentes/PaqueteDePresentacion'
import { useTrampaDeFoco } from '../ganchos/useTrampaDeFoco'
import { telon } from '../ganchos/telon'
import { AyudaDeCampo } from '../ayuda/AyudaDeCampo'

const NOMBRE: Record<TipoDeDeclaracion, string> = {
  IVA: 'IVA mensual',
  ISR: 'Pago provisional de ISR',
  DIOT: 'DIOT',
}

const ETAPA: Record<EtapaDeDeclaracion, { texto: string; clase: string }> = {
  PorGenerar: { texto: 'Por generar', clase: 'neutra' },
  Generada: { texto: 'Papel sellado · sin presentar', clase: 'aviso' },
  Presentada: { texto: 'Presentada · falta el acuse', clase: 'suave' },
  Acusada: { texto: 'Presentada con acuse', clase: 'vigente' },
}

/** La pantalla del papel donde se sella cada impuesto. La DIOT no tiene papel en ContaQuick. */
const PANTALLA_DEL_PAPEL: Partial<Record<TipoDeDeclaracion, string>> = { IVA: 'iva', ISR: 'isr' }

const SIN_PERMISO =
  'Tu rol no puede hacer este cambio: registrar y resguardar acuses es del administrador o del contador, y anular solo del administrador.'

const mensaje = (e: unknown, respaldo: string) =>
  e instanceof ErrorDeApi ? (e.estado === 403 ? SIN_PERMISO : e.message) : respaldo

const dinero = (n: number) => n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Los instantes llegan en UTC («Z») y se pintan en hora local (ver fechas.ts). */
const instante = (iso: string) => new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })

/** La fecha de hoy en hora local, como la pide un `<input type="date">`. */
const hoyLocal = () => {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function Papel({ titulo, papel }: { titulo: string; papel: ResumenDePapel }) {
  return (
    <p className="sutil">
      {titulo}: sellado el {instante(papel.generadoEnUtc)} · a pagar {dinero(papel.impuestoAPagar)}
      {papel.saldoAFavorDelPeriodo !== 0 && ` · saldo a favor ${dinero(papel.saldoAFavorDelPeriodo)}`}
    </p>
  )
}

/**
 * Control de presentación de declaraciones (RF-1605): por periodo, en qué va el IVA, el ISR y la
 * DIOT del cliente —papel sellado, presentada, acusada— y las presentaciones registradas con su
 * número de operación y su acuse.
 *
 * Registrar NO presenta nada ante el SAT (RN-62): el contador presenta en el portal con la e.firma
 * del contribuyente y aquí deja la evidencia. Las guardas de rol son presentación: el servidor
 * vuelve a revisar cada cambio.
 */
export function Declaraciones() {
  const { id: clienteId } = useParams<{ id: string }>()
  const hoy = new Date()
  const periodos = useMemo(() => periodosDisponibles(hoy), [hoy.getFullYear(), hoy.getMonth()])
  const [anio, setAnio] = useState(periodos[1].anio)
  const [mes, setMes] = useState(periodos[1].mes)
  const [cliente, setCliente] = useState<Cliente>()
  // undefined: todavía no se sabe; null: no se pudo saber, y entonces decide el servidor.
  const [rol, setRol] = useState<RolDeUsuario | null>()
  const [seguimiento, setSeguimiento] = useState<SeguimientoDeDeclaraciones>()
  const [errorCarga, setErrorCarga] = useState('')
  const [errorAccion, setErrorAccion] = useState('')
  const [aviso, setAviso] = useState('')
  const [registrando, setRegistrando] = useState<SeguimientoDeDeclaracion>()
  const [resguardando, setResguardando] = useState<PresentacionDeDeclaracion>()
  const [anulando, setAnulando] = useState<PresentacionDeDeclaracion>()

  const conocido = rol !== undefined
  const puedeRegistrar = conocido && (rol === null || rol === 'Administrador' || rol === 'Contador')
  const esAdministrador = conocido && (rol === null || rol === 'Administrador')

  async function cargar() {
    if (!clienteId) return
    setSeguimiento(undefined)
    setErrorCarga('')
    try {
      setSeguimiento(await api.obtenerSeguimientoDeDeclaraciones(clienteId, anio, mes))
    } catch (e) {
      setErrorCarga(mensaje(e, 'No se pudo cargar el control de declaraciones del periodo.'))
    }
  }

  useEffect(() => {
    if (!clienteId) return
    api.obtenerCliente(clienteId).then(setCliente).catch(() => setCliente(undefined))
    api.despachoActual().then((d) => setRol(d.rolActual)).catch(() => setRol(null))
  }, [clienteId])

  useEffect(() => {
    setAviso('')
    setErrorAccion('')
    void cargar()
  }, [clienteId, anio, mes])

  /** Al cerrar un modal: si trae aviso, el cambio se hizo y la pantalla se recarga. */
  function alCerrarModal(nuevoAviso?: string) {
    setRegistrando(undefined)
    setResguardando(undefined)
    setAnulando(undefined)
    if (nuevoAviso) {
      setAviso(nuevoAviso)
      void cargar()
    }
  }

  async function descargarAcuse(p: PresentacionDeDeclaracion) {
    if (!clienteId) return
    setErrorAccion('')
    try {
      await api.descargarAcuseDeDeclaracion(clienteId, p.id)
    } catch (e) {
      setErrorAccion(mensaje(e, 'No se pudo descargar el acuse.'))
    }
  }

  if (!clienteId) return <p>Cargando…</p>

  return (
    <>
      <p className="sutil"><Link to={`/clientes/${clienteId}`}>← {cliente?.razonSocial ?? 'Expediente'}</Link></p>

      <header className="encabezado linea-base">
        <h1>Declaraciones</h1>
        <select
          className="compacta"
          style={{ width: 'auto' }}
          aria-label="Periodo"
          value={`${anio}-${mes}`}
          onChange={(e) => {
            const [a, m] = e.target.value.split('-')
            setAnio(Number(a))
            setMes(Number(m))
          }}
        >
          {periodos.map((p) => (
            <option key={`${p.anio}-${p.mes}`} value={`${p.anio}-${p.mes}`}>{nombreDePeriodo(p.anio, p.mes)}</option>
          ))}
        </select>
        {cliente && <span className="encabezado-sub mono">{cliente.rfc} · {cliente.razonSocial}</span>}
      </header>

      <p className="nota" data-recorrido="declaraciones">
        Aquí registras lo que ya presentaste en el portal del SAT: la fecha, el número de operación y el
        acuse. Registrar no presenta nada ante el SAT: eso lo haces tú en el portal, con la e.firma del
        contribuyente (RN-62).
      </p>

      <PaqueteDeDeclaraciones clienteId={clienteId} anio={anio} mes={mes} />
      {/* RF-1607 · Todo lo que falta presentar del mes, también el Anexo 24, con la guía de cada archivo. */}
      <PaqueteDePresentacion clienteId={clienteId} anio={anio} mes={mes} />

      {errorCarga && <p className="error" role="alert">{errorCarga}</p>}
      {errorAccion && <p className="error" role="alert">{errorAccion}</p>}
      {aviso && <p className="aviso" role="status">{aviso}</p>}
      {!seguimiento && !errorCarga && <p className="sutil">Cargando…</p>}

      {seguimiento?.declaraciones.map((d) => (
        <TarjetaDeDeclaracion
          key={d.declaracion}
          clienteId={clienteId}
          anio={seguimiento.anio}
          mes={seguimiento.mes}
          d={d}
          puedeRegistrar={puedeRegistrar}
          esAdministrador={esAdministrador}
          alRegistrar={() => { setErrorAccion(''); setRegistrando(d) }}
          alResguardar={(p) => { setErrorAccion(''); setResguardando(p) }}
          alAnular={(p) => { setErrorAccion(''); setAnulando(p) }}
          alDescargar={(p) => void descargarAcuse(p)}
        />
      ))}

      {registrando && seguimiento && (
        <RegistroDePresentacion
          clienteId={clienteId}
          anio={seguimiento.anio}
          mes={seguimiento.mes}
          d={registrando}
          alCerrar={alCerrarModal}
        />
      )}
      {resguardando && <ResguardoDeAcuse clienteId={clienteId} presentacion={resguardando} alCerrar={alCerrarModal} />}
      {anulando && <AnulacionDePresentacion clienteId={clienteId} presentacion={anulando} alCerrar={alCerrarModal} />}
    </>
  )
}

function TarjetaDeDeclaracion({
  clienteId,
  anio,
  mes,
  d,
  puedeRegistrar,
  esAdministrador,
  alRegistrar,
  alResguardar,
  alAnular,
  alDescargar,
}: {
  clienteId: string
  anio: number
  mes: number
  d: SeguimientoDeDeclaracion
  puedeRegistrar: boolean
  esAdministrador: boolean
  alRegistrar: () => void
  alResguardar: (p: PresentacionDeDeclaracion) => void
  alAnular: (p: PresentacionDeDeclaracion) => void
  alDescargar: (p: PresentacionDeDeclaracion) => void
}) {
  const nombre = NOMBRE[d.declaracion]
  // La DIOT no se genera en ContaQuick: «por generar» diría algo que no le toca.
  const etapa = d.declaracion === 'DIOT' && d.etapa === 'PorGenerar' ? { texto: 'Sin registrar', clase: 'neutra' } : ETAPA[d.etapa]
  const pantallaDelPapel = PANTALLA_DEL_PAPEL[d.declaracion]
  const idTitulo = `declaracion-${d.declaracion}`

  return (
    <section aria-labelledby={idTitulo} style={{ marginTop: 'var(--space-6)' }}>
      <h2 id={idTitulo}>
        {nombre} <span className={`insignia ${etapa.clase}`}>{etapa.texto}</span>
      </h2>

      {d.explicacion && <p className="sutil">{d.explicacion}</p>}
      {d.fechaLimite && (
        <p className="sutil">
          Fecha límite: {fechaContable(d.fechaLimite)}{d.origenDelPlazo && ` · ${d.origenDelPlazo}`}
        </p>
      )}

      {d.papelVigente && <Papel titulo="Papel vigente" papel={d.papelVigente} />}
      {d.papelPresentado && d.papelPresentado.id !== d.papelVigente?.id && (
        <Papel titulo="Papel presentado" papel={d.papelPresentado} />
      )}
      {d.papelMasRecienteQueElPresentado && (
        <p className="aviso" role="status">
          Sellaste un papel de {d.declaracion} después del que presentaste: si cambia lo declarado, procede una
          complementaria.
        </p>
      )}
      {!d.papelVigente && d.etapa === 'PorGenerar' && pantallaDelPapel && (
        <p className="sutil">
          <Link to={`/clientes/${clienteId}/${pantallaDelPapel}`}>Ir al papel de {d.declaracion}</Link>
        </p>
      )}

      {d.presentaciones.length === 0 ? (
        <p className="vacio">No hay presentaciones registradas de {nombre} en {nombreDePeriodo(anio, mes)}.</p>
      ) : (
        <div className="tabla-envoltura">
          <table className="tabla" aria-label={`Presentaciones de ${nombre}`}>
            <thead>
              <tr>
                <th scope="col">Carácter</th>
                <th scope="col">Presentada el</th>
                <th scope="col">Número de operación</th>
                <th scope="col">Acuse</th>
                {esAdministrador && <th scope="col">Acciones</th>}
              </tr>
            </thead>
            <tbody>
              {d.presentaciones.map((p) => (
                <tr key={p.id} className={p.vigente ? undefined : 'sutil'}>
                  <td>
                    {p.caracter}
                    {!p.vigente && (
                      <div style={{ fontSize: 11 }}>
                        Anulada{p.anuladaEnUtc && ` el ${instante(p.anuladaEnUtc)}`}
                        {p.motivoDeAnulacion && ` · motivo: ${p.motivoDeAnulacion}`}
                      </div>
                    )}
                  </td>
                  <td>
                    {fechaContable(p.fechaDePresentacion)}
                    {p.despuesDelPlazo && <> <span className="insignia aviso">después del plazo</span></>}
                  </td>
                  <td className="mono">{p.vigente ? p.numeroDeOperacion : <s>{p.numeroDeOperacion}</s>}</td>
                  <td>
                    {p.nombreDelAcuse ? (
                      <button type="button" className="enlace" onClick={() => alDescargar(p)}>
                        Descargar {p.nombreDelAcuse}
                      </button>
                    ) : p.vigente && puedeRegistrar ? (
                      <button type="button" className="secundario" onClick={() => alResguardar(p)}>Resguardar acuse</button>
                    ) : (
                      <span className="sutil">Sin acuse</span>
                    )}
                  </td>
                  {esAdministrador && (
                    <td>
                      {p.vigente && (
                        <button type="button" className="secundario" onClick={() => alAnular(p)}>Anular</button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {puedeRegistrar && (
        <div className="acciones">
          <button type="button" className="primario" data-recorrido="declaraciones-registrar" onClick={alRegistrar}>Registrar presentación de {d.declaracion}</button>
        </div>
      )}
    </section>
  )
}

function RegistroDePresentacion({
  clienteId,
  anio,
  mes,
  d,
  alCerrar,
}: {
  clienteId: string
  anio: number
  mes: number
  d: SeguimientoDeDeclaracion
  alCerrar: (aviso?: string) => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLFormElement>(true, alCerrar)
  // Solo una normal vigente por declaración y periodo: si ya la hay, lo que sigue es complementaria.
  const yaHayNormal = d.presentaciones.some((p) => p.vigente && p.caracter === 'Normal')
  const [caracter, setCaracter] = useState<CaracterDeDeclaracion>(yaHayNormal ? 'Complementaria' : 'Normal')
  const [fecha, setFecha] = useState('')
  const [numero, setNumero] = useState('')
  const [archivo, setArchivo] = useState<File | null>(null)
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)
  const nombre = NOMBRE[d.declaracion]

  async function guardar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    setError('')
    setEnviando(true)
    let registrada: PresentacionDeDeclaracion
    try {
      registrada = await api.registrarPresentacionDeDeclaracion(clienteId, {
        anio, mes, declaracion: d.declaracion, caracter, fechaDePresentacion: fecha, numeroDeOperacion: numero.trim(),
      })
    } catch (e) {
      setError(mensaje(e, 'No se pudo registrar la presentación.'))
      setEnviando(false)
      return
    }

    const hecho = `Registraste la presentación ${caracter === 'Normal' ? 'normal' : 'complementaria'} de ${nombre} de ` +
      `${nombreDePeriodo(anio, mes)} (operación ${registrada.numeroDeOperacion}).`
    if (!archivo) {
      alCerrar(hecho)
      return
    }
    // El acuse va después del registro: si falla, la presentación ya quedó y no se pierde lo tecleado.
    try {
      await api.resguardarAcuseDeDeclaracion(clienteId, registrada.id, archivo)
      alCerrar(`${hecho} Acuse resguardado: ${archivo.name}.`)
    } catch (e) {
      alCerrar(`${hecho} El acuse no se resguardó: ${mensaje(e, 'falló la subida')}. Resguárdalo desde su fila.`)
    }
  }

  return (
    <div className="modal" {...telon(() => alCerrar())}>
      <form
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        onSubmit={guardar}
      >
        <h2 id={idTitulo}>Registrar presentación de {nombre}</h2>
        <p className="sutil">
          Periodo {nombreDePeriodo(anio, mes)}. Anota lo que ya presentaste en el portal del SAT: registrar no
          presenta nada (RN-62).
          {d.declaracion === 'DIOT'
            ? ' La DIOT no tiene papel en ContaQuick: aquí solo resguardas su evidencia.'
            : d.papelVigente
              ? ` Se liga al papel vigente, sellado el ${instante(d.papelVigente.generadoEnUtc)}.`
              : ' No hay papel sellado de este periodo: la presentación queda sin papel ligado.'}
        </p>

        <label>
          Carácter
          <select value={caracter} onChange={(e) => setCaracter(e.target.value as CaracterDeDeclaracion)}>
            <option value="Normal">Normal</option>
            <option value="Complementaria">Complementaria</option>
          </select>
        </label>
        {yaHayNormal && caracter === 'Normal' && (
          <p className="aviso" role="status">
            Ya hay una normal vigente de este periodo: si volviste a presentar, regístrala como complementaria.
          </p>
        )}

        <label>
          Fecha de presentación (la del acuse)
          <input type="date" value={fecha} max={hoyLocal()} onChange={(e) => setFecha(e.target.value)} required />
        </label>
        <div className="con-ayuda">
          <label>
            Número de operación
            <input className="mono" value={numero} maxLength={30} onChange={(e) => setNumero(e.target.value)} required />
          </label>
          <AyudaDeCampo clave="numero-de-operacion" />
        </div>
        <label>
          Acuse del SAT (PDF o ZIP, opcional)
          <input
            type="file"
            accept=".pdf,.zip,application/pdf,application/zip"
            onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
          />
        </label>
        <p className="menuda">Si ahora no lo tienes, lo resguardas después desde la fila de la presentación.</p>

        {error && <p className="error" role="alert">{error}</p>}

        <div className="acciones">
          <button type="button" onClick={() => alCerrar()}>Cancelar</button>
          <button type="submit" className="primario" disabled={enviando}>
            {enviando ? 'Registrando…' : 'Registrar presentación'}
          </button>
        </div>
      </form>
    </div>
  )
}

function ResguardoDeAcuse({
  clienteId,
  presentacion,
  alCerrar,
}: {
  clienteId: string
  presentacion: PresentacionDeDeclaracion
  alCerrar: (aviso?: string) => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLFormElement>(true, alCerrar)
  const [archivo, setArchivo] = useState<File | null>(null)
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)

  async function guardar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    if (!archivo) {
      setError('Elige el archivo del acuse que te entregó el SAT (PDF o ZIP).')
      return
    }
    setError('')
    setEnviando(true)
    try {
      await api.resguardarAcuseDeDeclaracion(clienteId, presentacion.id, archivo)
      alCerrar(`Acuse resguardado: ${archivo.name}.`)
    } catch (e) {
      setError(mensaje(e, 'No se pudo resguardar el acuse.'))
      setEnviando(false)
    }
  }

  return (
    <div className="modal" {...telon(() => alCerrar())}>
      <form
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        onSubmit={guardar}
      >
        <h2 id={idTitulo}>Resguardar acuse de {NOMBRE[presentacion.declaracion]}</h2>
        <p className="sutil">
          {presentacion.caracter} · operación <span className="mono">{presentacion.numeroDeOperacion}</span> · presentada
          el {fechaContable(presentacion.fechaDePresentacion)}.
        </p>
        <label>
          Archivo del acuse (PDF o ZIP)
          <input
            type="file"
            accept=".pdf,.zip,application/pdf,application/zip"
            onChange={(e) => setArchivo(e.target.files?.[0] ?? null)}
          />
        </label>
        <p className="menuda">
          Se conserva íntegro al menos cinco años (RN-09) y no se reemplaza (RN-61): revisa que sea el acuse
          correcto antes de guardarlo.
        </p>

        {error && <p className="error" role="alert">{error}</p>}

        <div className="acciones">
          <button type="button" onClick={() => alCerrar()}>Cancelar</button>
          <button type="submit" className="primario" disabled={enviando}>
            {enviando ? 'Guardando…' : 'Resguardar acuse'}
          </button>
        </div>
      </form>
    </div>
  )
}

function AnulacionDePresentacion({
  clienteId,
  presentacion,
  alCerrar,
}: {
  clienteId: string
  presentacion: PresentacionDeDeclaracion
  alCerrar: (aviso?: string) => void
}) {
  const idTitulo = useId()
  const panelRef = useTrampaDeFoco<HTMLFormElement>(true, alCerrar)
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)

  async function anular(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    if (!motivo.trim()) {
      setError('Escribe por qué anulas el registro: queda junto a él y en la bitácora del despacho.')
      return
    }
    setError('')
    setEnviando(true)
    try {
      await api.anularPresentacionDeDeclaracion(clienteId, presentacion.id, motivo.trim())
      alCerrar(`Anulaste el registro de la operación ${presentacion.numeroDeOperacion}. Se conserva como rastro con su motivo.`)
    } catch (e) {
      setError(mensaje(e, 'No se pudo anular el registro.'))
      setEnviando(false)
    }
  }

  return (
    <div className="modal" {...telon(() => alCerrar())}>
      <form
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        className="panel"
        onClick={(e) => e.stopPropagation()}
        onSubmit={anular}
      >
        <h2 id={idTitulo}>Anular el registro de {NOMBRE[presentacion.declaracion]}</h2>
        <p className="sutil">
          Operación <span className="mono">{presentacion.numeroDeOperacion}</span> · {presentacion.caracter}. Anula solo un
          registro equivocado: no cancela nada ante el SAT, y el registro anulado se sigue viendo con su motivo.
        </p>
        <label>
          Motivo de la anulación
          <textarea value={motivo} maxLength={500} onChange={(e) => setMotivo(e.target.value)} />
        </label>

        {error && <p className="error" role="alert">{error}</p>}

        <div className="acciones">
          <button type="button" onClick={() => alCerrar()}>Cancelar</button>
          <button type="submit" className="primario" disabled={enviando}>
            {enviando ? 'Anulando…' : 'Anular el registro'}
          </button>
        </div>
      </form>
    </div>
  )
}
